import React, { useState, useRef, useEffect } from 'react';
import { Video, Upload, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../ui/BottomSheet';

interface VideoIntroModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'upload' | 'playback';
  videoUrl?: string;
  onVideoUploaded?: (url: string) => void;
}

const brandingGreen = '#2F4F3E';

export function VideoIntroModal({
  isOpen,
  onClose,
  mode,
  videoUrl,
  onVideoUploaded
}: VideoIntroModalProps) {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!isOpen && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isOpen]);

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      alert('Please select a video file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      alert('Video must be less than 100MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = URL.createObjectURL(file);

      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          if (video.duration > 15) {
            reject(new Error('Video must be 15 seconds or less'));
          } else {
            resolve();
          }
        };
        video.onerror = () => {
          URL.revokeObjectURL(video.src);
          reject(new Error('Failed to load video'));
        };
      });
    } catch (error: unknown) {
      const err = error as Error;
      if (err.message === 'Video must be 15 seconds or less') {
        alert('Video must be 15 seconds or less. Please trim your video and try again.');
        setIsUploading(false);
        return;
      }
      console.error('Error validating video:', error);
    }

    setUploadProgress(30);

    try {
      const publicUrl = await uploadVideo(file);

      if (publicUrl && onVideoUploaded) {
        setUploadProgress(100);
        onVideoUploaded(publicUrl);
        onClose();
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Failed to upload video. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const uploadVideo = async (file: File): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const fileName = `video-intro-${user.id}-${Date.now()}.${file.name.split('.').pop()}`;
      const filePath = `videos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('listing-images')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('listing-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error in uploadVideo:', error);
      return null;
    }
  };

  const handleDeleteVideo = async () => {
    if (!videoUrl || !onVideoUploaded) return;

    try {
      const urlParts = videoUrl.split('/storage/v1/object/public/');
      if (urlParts.length > 1) {
        const filePath = urlParts[1];
        await supabase.storage.from('listing-images').remove([filePath]);
      }

      onVideoUploaded('');
      onClose();
    } catch (error) {
      console.error('Error deleting video:', error);
    }
  };

  const sheetTitle =
    mode === 'upload'
      ? videoUrl
        ? t('profile.changeVideoIntro')
        : t('profile.addVideoIntro')
      : t('profile.videoIntroduction');

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="font-gelica text-lg" style={{ color: brandingGreen }}>
          {sheetTitle}
        </span>
      }
      desktopMode="modal"
      desktopMaxWidthClass="max-w-lg"
      maxHeight={mode === 'playback' ? 92 : 88}
      panelClassName="bg-gradient-to-b from-[#F3F7EE] via-white to-white border-t border-accent-text/10 lg:border-0 shadow-2xl"
      contentClassName="px-0"
    >
      <div className="px-5 pb-6 pt-1">
        {mode === 'upload' ? (
          <>
            <div className="mb-5 p-4 rounded-2xl border border-accent-text/15 bg-white/80 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-text/15">
                  <Video className="w-5 h-5 text-accent-text" />
                </div>
                <div>
                  <p className="font-semibold text-sm font-figtree text-[#2F4F3E]">
                    {t('profile.showCommunityWhoYouAre')}
                  </p>
                  <p className="text-sm mt-1 text-content/65 font-figtree leading-relaxed">
                    {t('profile.videoIntroDescription')}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-2 border-dashed border-accent-text/25 rounded-2xl p-8 text-center bg-white/60 hover:border-accent-text/45 transition-colors">
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="hidden"
                id="video-upload-intro"
                disabled={isUploading}
              />
              <label htmlFor="video-upload-intro" className="cursor-pointer block">
                {isUploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-10 h-10 animate-spin mb-3 text-accent-text" />
                    <p className="text-sm font-medium text-[#2F4F3E] font-figtree">
                      {t('profile.uploadingVideo')}
                    </p>
                    {uploadProgress > 0 && (
                      <div className="w-48 h-2 bg-gray-200 rounded-full mt-3 overflow-hidden">
                        <div
                          className="h-full transition-all duration-300 bg-accent-text rounded-full"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 bg-accent-text/15">
                      <Upload className="w-6 h-6 text-accent-text" />
                    </div>
                    <p className="font-semibold text-sm text-[#2F4F3E] font-figtree">
                      {t('profile.clickToUploadVideo')}
                    </p>
                    <p className="text-xs mt-1.5 text-content/55 font-figtree">
                      {t('profile.videoFormatInfo')}
                    </p>
                  </>
                )}
              </label>
            </div>

            {videoUrl && (
              <button
                type="button"
                onClick={handleDeleteVideo}
                className="mt-4 w-full py-3 text-sm rounded-xl transition-colors font-medium font-figtree text-red-600 hover:bg-red-50"
              >
                {t('profile.removeVideoIntro')}
              </button>
            )}
          </>
        ) : (
          <>
            {videoUrl && (
              <div className="rounded-2xl overflow-hidden bg-black ring-2 ring-accent-text/20 shadow-lg">
                <video
                  ref={videoRef}
                  src={videoUrl}
                  controls
                  playsInline
                  className="w-full max-h-[min(65vh,520px)] object-contain mx-auto bg-black"
                />
              </div>
            )}
            <p className="mt-4 text-center text-xs text-content/50 font-figtree px-2">
              {t('profile.videoIntroPlaybackHint')}
            </p>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
